import { useEffect } from "react";
import queryString from "query-string";
import { OktaLogoIcon } from "vega-icons";

import { useGetRedirectUrl } from "@vega-api/generated/idp-v1/idp-v1";
import { type FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { OauthProvider } from "@vega-api/generated/types/oauthProvider";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { getBaseURL, isLocal, isLocalApi } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { CommonPaths } from "../../paths";

type Props = {
  source: FrontendPortal;
  variant?: "primary" | "secondary";
};

const getRedirectUrl = (url: string) => {
  if (isLocal() && !isLocalApi) {
    const urlObj = new URL(url);

    urlObj.searchParams.set(
      "redirect_uri",
      `${getBaseURL()}${CommonPaths.OktaAuth}`,
    );

    return urlObj.toString();
  }

  return url;
};

export const OktaLoginButton = ({ source, variant = "primary" }: Props) => {
  const showToast = useToast();
  const { data, isError, isLoading } = useGetRedirectUrl({ source });

  useEffect(() => {
    if ((isError || !data?.ssoUrl) && !isLoading) {
      showToast({
        title: m.error_unableToRetrieveOktaURL(),
        description: m.error_unableToRetrieveOktaURLDESC(),
        variant: "destructive",
        toastId: "unable-to-retrieve-okta-url",
      });
    }
  }, [isError, data, showToast, isLoading]);
  const params = {
    source,
    oauthProvider: OauthProvider.OKTA,
    redirectToLocalhost: isLocal(),
  };

  const url = data?.ssoUrl
    ? getRedirectUrl(data.ssoUrl)
    : `${getBaseURL()}/api/authentication/v1/login/idp?${queryString.stringify(params)}`;

  const handleClick = () => {
    LocalStorageService.removeAllTokens();
    window.location.assign(url);
  };

  return (
    <ThemedButton
      data-analytics="gtm-okta-authorize"
      dataTestId="sign-in-button"
      disabled={isFalsy(source)}
      fullWidth
      icon={<OktaLogoIcon />}
      isLoading={isLoading}
      onClick={handleClick}
      variant={variant}
    >
      {m.auth_login_with_okta()}
    </ThemedButton>
  );
};
